import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { cn } from '@/lib/utils';

export type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount?: number;
  pageSize?: number;
  maxVisiblePages?: number;
  isLoading?: boolean;
  className?: string;
  showSummary?: boolean;
  summaryText?: string;
  /** When false, still renders for a single page (disabled prev/next). Default true. */
  hideWhenSinglePage?: boolean;
  layout?: 'stacked' | 'footer';
};

/**
 * Shared pagination UI — Figma **Figma-DEV** `53:3260` (pills + `#20AEDC` active); nav uses MUI `ArrowBack` / `ArrowForward`.
 * Use wherever lists are paginated (`RecipesPage`, `ClientGrid`, meal plans, etc.).
 */
const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  pageSize,
  maxVisiblePages = 5,
  isLoading = false,
  className,
  showSummary = true,
  summaryText,
  hideWhenSinglePage = true,
  layout = 'stacked',
}) => {
  if (!totalPages || totalPages < 1) {
    return null;
  }
  if (hideWhenSinglePage && totalPages <= 1) {
    return null;
  }

  const clampedPage = Math.min(Math.max(currentPage, 1), totalPages);
  const visiblePageCount = Math.max(1, Math.min(maxVisiblePages, totalPages));

  const startPage = Math.max(
    1,
    Math.min(clampedPage - Math.floor(visiblePageCount / 2), totalPages - visiblePageCount + 1),
  );
  const pageNumbers = Array.from({ length: visiblePageCount }, (_, idx) => startPage + idx);
  const isPreviousDisabled = isLoading || clampedPage <= 1;
  const isNextDisabled = isLoading || clampedPage >= totalPages;

  const handleChange = (page: number) => {
    if (page === clampedPage || page < 1 || page > totalPages || isLoading) {
      return;
    }
    onPageChange(page);
  };

  const computedSummaryText = (() => {
    if (summaryText) {
      return summaryText;
    }

    if (typeof totalCount === 'number' && totalCount >= 0) {
      if (typeof pageSize === 'number' && pageSize > 0 && totalCount > 0) {
        const startItem = (clampedPage - 1) * pageSize + 1;
        const endItem = Math.min(clampedPage * pageSize, totalCount);
        return `Page ${clampedPage} of ${totalPages} • Showing ${startItem}-${endItem} of ${totalCount}`;
      }

      return `Page ${clampedPage} of ${totalPages} • ${totalCount} total`;
    }

    return `Page ${clampedPage} of ${totalPages}`;
  })();
  const isFooterLayout = layout === 'footer';

  return (
    <div
      className={cn(
        isFooterLayout
          ? 'flex w-full flex-col gap-4 border-t border-[#edf1f5] bg-white px-6 py-6 sm:flex-row sm:items-center sm:justify-between'
          : 'flex flex-col items-center gap-2',
        className,
      )}
      data-node-id="53:3260"
    >
      {isFooterLayout && showSummary ? (
        <p className="text-sm font-medium leading-6 text-[#61718c] sm:text-[15px]">{computedSummaryText}</p>
      ) : null}

      <nav
        role="navigation"
        aria-label="Pagination"
        className={cn(
          'inline-flex items-center gap-[10px]',
          isFooterLayout && 'self-end sm:self-auto',
          isLoading && 'pointer-events-none opacity-60',
        )}
      >
        <button
          type="button"
          aria-label="Previous page"
          disabled={isPreviousDisabled}
          onClick={() => handleChange(clampedPage - 1)}
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-md text-[#2a2a2a] transition-colors',
            'hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC]/35',
            isPreviousDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
          )}
        >
          <ArrowBackIcon sx={{ fontSize: 24, color: 'currentColor' }} aria-hidden />
        </button>

        <div className="flex items-center gap-[10px]">
          {pageNumbers.map((page) => {
            const isActive = page === clampedPage;
            return (
              <button
                key={page}
                type="button"
                aria-label={`Page ${page}`}
                aria-current={isActive ? 'page' : undefined}
                disabled={isLoading}
                onClick={() => handleChange(page)}
                className={cn(
                  'flex min-h-7 min-w-[29px] shrink-0 items-center justify-center rounded-lg px-3.5 py-0.5 text-sm leading-[1.5] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC]/35',
                  isActive
                    ? 'bg-white font-bold text-[#20AEDC] shadow-sm'
                    : 'bg-white/50 font-medium text-[rgba(42,42,42,0.5)] hover:bg-white/90 hover:text-[rgba(42,42,42,0.72)]',
                )}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Next page"
          disabled={isNextDisabled}
          onClick={() => handleChange(clampedPage + 1)}
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-md text-[#2a2a2a] transition-colors',
            'hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC]/35',
            isNextDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
          )}
        >
          <ArrowForwardIcon sx={{ fontSize: 24, color: 'currentColor' }} aria-hidden />
        </button>
      </nav>

      {!isFooterLayout && showSummary ? (
        <p className="text-center text-sm text-muted-foreground">{computedSummaryText}</p>
      ) : null}
    </div>
  );
};

export default PaginationControls;

/** Alias for explicit imports where “app” naming is preferred. */
export { PaginationControls as AppPagination };
